import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../hooks/useAuth';
import { isAdminUser } from '../../types/auth';
import { adminService } from '../../services/admin';
import {
  ImportOptions,
  ExcelImportResponse,
  NormalizedRegisterImportResponse,
  ImportError,
} from '../../types/excelImport';
import { MembershipType } from '../../types/member';
import { Container } from '../../design-system/components/Layout';
import { Card } from '../../design-system/components/Card';
import { Button } from '../../design-system/components/Button';
import { Heading, Text } from '../../design-system/components/Typography';
import { PageHeader } from '../../design-system/patterns/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { FileUpload } from '../../components/common/FileUpload';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';

const PageContainer = styled(Container)`
  max-width: 1200px;
  margin: 0 auto;
  padding: ${spacing[6]};
`;

const SectionCard = styled(Card)`
  padding: ${spacing[8]};
  margin-bottom: ${spacing[6]};
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${spacing[4]};
  margin-top: ${spacing[4]};
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  cursor: pointer;
  font-size: ${typography.fontSize.base};
  color: ${props => props.theme.colors.text};
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const Select = styled.select`
  padding: ${spacing[2]} ${spacing[3]};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  border-radius: ${borders.radius.md};
  font-size: ${typography.fontSize.base};
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  min-width: 200px;
`;

const ResultsCard = styled(SectionCard)`
  background: ${props => props.theme.colors.surface};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${spacing[4]};
  margin-bottom: ${spacing[6]};
`;

const StatCard = styled.div`
  padding: ${spacing[4]};
  background: ${props => props.theme.colors.overlayLight};
  border-radius: ${borders.radius.lg};
  text-align: center;
`;

const StatValue = styled.div`
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
`;

const ErrorTableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  margin-top: ${spacing[4]};
`;

const ErrorTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const ErrorTableHeader = styled.thead`
  background: ${props => props.theme.colors.overlayLight};
`;

const ErrorTableRow = styled.tr`
  border-bottom: ${borders.width.base} solid ${props => props.theme.colors.border};
  
  &:hover {
    background: ${props => props.theme.colors.overlayLight};
  }
`;

const ErrorTableCell = styled.td`
  padding: ${spacing[3]};
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme.colors.text};
`;

const ErrorMessage = styled(ErrorTableCell)`
  color: ${props => props.theme.colors.error};
`;

const ColumnMappingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  margin-top: ${spacing[4]};
`;

const MappingItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${spacing[2]};
  background: ${props => props.theme.colors.overlayLight};
  border-radius: ${borders.radius.md};
  font-size: ${typography.fontSize.sm};
`;

type ImportResultView =
  | { kind: 'catalog'; data: ExcelImportResponse }
  | { kind: 'normalized'; data: NormalizedRegisterImportResponse };

const ExcelImport: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<ImportResultView | null>(null);
  const [options, setOptions] = useState<ImportOptions>({
    skipDuplicates: true,
    updateExisting: false,
    createUserAccounts: false,
    defaultRegistrationYear: new Date().getFullYear(),
    defaultMembershipType: MembershipType.Regular,
    useCatalogFormat: false,
    useNormalizedFormat: true,
    normalizedMemberType: 'Individual',
  });

  if (!isAdminUser(user)) {
    return null;
  }

  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
    setResults(null);
  };

  const handleFileRemove = () => {
    setFile(null);
    setResults(null);
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setResults(null);

    try {
      if (options.useNormalizedFormat) {
        const memberType = options.normalizedMemberType ?? 'Individual';
        const response = await adminService.importNormalizedRegister(file, memberType);
        setResults({ kind: 'normalized', data: response });
      } else {
        const response = await adminService.importMembers(file, {
          ...options,
          useNormalizedFormat: false,
        });
        setResults({ kind: 'catalog', data: response });
      }
    } catch (error) {
      console.error('Import error:', error);
      const axiosErr = error as { response?: { data?: NormalizedRegisterImportResponse | ExcelImportResponse | { message?: string }; status?: number }; message?: string };
      const data = axiosErr.response?.data;
      if (data && typeof data === 'object' && 'errors' in data && Array.isArray((data as ExcelImportResponse).errors)) {
        if ('createdCount' in data || 'readyRows' in data) {
          setResults({ kind: 'normalized', data: data as NormalizedRegisterImportResponse });
        } else {
          setResults({ kind: 'catalog', data: data as ExcelImportResponse });
        }
      } else {
        const msg =
          (data && typeof data === 'object' && 'message' in data && typeof (data as { message?: string }).message === 'string'
            ? (data as { message: string }).message
            : null)
          || (axiosErr.message ?? 'Import failed');
        setResults({
          kind: 'catalog',
          data: {
            totalRows: 0,
            successCount: 0,
            errorCount: 1,
            skippedCount: 0,
            updatedCount: 0,
            errors: [{
              rowNumber: 0,
              errorMessage: msg,
            }],
            columnMappings: [],
            importDate: new Date().toISOString(),
          },
        });
      }
    } finally {
      setImporting(false);
    }
  };

  const errors: ImportError[] =
    results?.kind === 'catalog'
      ? results.data.errors
      : results?.kind === 'normalized'
        ? results.data.errors
        : [];

  return (
    <PageContainer>
      <PageHeader
        title={t('admin.excelImport.title')}
        subtitle={t('admin.excelImport.subtitle')}
      />

      <SectionCard>
        <Heading size="lg" marginBottom={spacing[4]}>
          {t('admin.excelImport.uploadFile')}
        </Heading>
        <FileUpload
          onFileSelect={handleFileSelect}
          onFileRemove={handleFileRemove}
          accept={options.useNormalizedFormat ? '.xlsx' : '.xls,.xlsx'}
          maxSize={15 * 1024 * 1024}
          currentFile={file}
          label={t('admin.excelImport.selectFile')}
          hint={t('admin.excelImport.fileHint')}
        />
      </SectionCard>

      <SectionCard>
        <Heading size="lg" marginBottom={spacing[4]}>
          {t('admin.excelImport.importOptions')}
        </Heading>
        <OptionsGrid>
          <CheckboxLabel>
            <Checkbox
              type="checkbox"
              checked={!!options.useNormalizedFormat}
              onChange={(e) => setOptions({
                ...options,
                useNormalizedFormat: e.target.checked,
                useCatalogFormat: e.target.checked ? false : options.useCatalogFormat,
              })}
            />
            <span>{t('admin.excelImport.useNormalizedFormat')}</span>
          </CheckboxLabel>
          {!options.useNormalizedFormat && (
            <>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  checked={options.skipDuplicates}
                  onChange={(e) => setOptions({ ...options, skipDuplicates: e.target.checked })}
                />
                <span>{t('admin.excelImport.skipDuplicates')}</span>
              </CheckboxLabel>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  checked={options.updateExisting}
                  onChange={(e) => setOptions({ ...options, updateExisting: e.target.checked })}
                />
                <span>{t('admin.excelImport.updateExisting')}</span>
              </CheckboxLabel>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  checked={options.createUserAccounts}
                  onChange={(e) => setOptions({ ...options, createUserAccounts: e.target.checked })}
                />
                <span>{t('admin.excelImport.createUserAccounts')}</span>
              </CheckboxLabel>
              <CheckboxLabel>
                <Checkbox
                  type="checkbox"
                  checked={!!options.useCatalogFormat}
                  onChange={(e) => setOptions({ ...options, useCatalogFormat: e.target.checked })}
                />
                <span>{t('admin.excelImport.useCatalogFormat')}</span>
              </CheckboxLabel>
            </>
          )}
        </OptionsGrid>

        {options.useNormalizedFormat ? (
          <div style={{ marginTop: spacing[4] }}>
            <Text size="sm" marginBottom={spacing[1]}>
              {t('admin.excelImport.memberType')}
            </Text>
            <Select
              value={options.normalizedMemberType}
              onChange={(e) => setOptions({
                ...options,
                normalizedMemberType: e.target.value as 'Individual' | 'Organization',
              })}
            >
              <option value="Individual">{t('admin.excelImport.memberTypeIndividual')}</option>
              <option value="Organization">{t('admin.excelImport.memberTypeOrganization')}</option>
            </Select>
            <Text size="sm" color="secondary" style={{ marginTop: spacing[3], display: 'block' }}>
              {t('admin.excelImport.normalizedHint')}
            </Text>
            <Text size="sm" color="secondary" style={{ marginTop: spacing[2], display: 'block' }}>
              {t('admin.excelImport.templateSchemaHint', {
                defaultValue:
                  'Download a blank 55-column sample workbook with the required sheet name and headers. Column types and required fields are listed in the hint above and schema documentation.',
              })}
            </Text>
            <div style={{ marginTop: spacing[3] }}>
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  try {
                    const blob = await adminService.downloadNormalizedImportTemplate(
                      options.normalizedMemberType ?? 'Individual'
                    );
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download =
                      (options.normalizedMemberType ?? 'Individual') === 'Organization'
                        ? 'ICOM-Greece-Organizations-import-template.xlsx'
                        : 'ICOM-Greece-Individual-Members-import-template.xlsx';
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (err) {
                    console.error('Template download failed', err);
                  }
                }}
              >
                {t('admin.excelImport.downloadTemplate', {
                  defaultValue: 'Download sample workbook',
                })}
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: spacing[4], display: 'flex', gap: spacing[4], flexWrap: 'wrap' }}>
            <div>
              <Text size="sm" marginBottom={spacing[1]}>
                {t('admin.excelImport.defaultRegistrationYear')}
              </Text>
              <Select
                value={options.defaultRegistrationYear}
                onChange={(e) => setOptions({ ...options, defaultRegistrationYear: parseInt(e.target.value) })}
              >
                {Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </Select>
            </div>
            <div>
              <Text size="sm" marginBottom={spacing[1]}>
                {t('admin.excelImport.defaultMembershipType')}
              </Text>
              <Select
                value={options.defaultMembershipType}
                onChange={(e) => setOptions({ ...options, defaultMembershipType: parseInt(e.target.value) as MembershipType })}
              >
                <option value={MembershipType.Regular}>{t('membershipType.regular')}</option>
                <option value={MembershipType.Student}>{t('membershipType.student')}</option>
                <option value={MembershipType.Retired}>{t('membershipType.retired')}</option>
              </Select>
            </div>
          </div>
        )}
      </SectionCard>

      {results?.kind === 'catalog' && results.data.columnMappings.length > 0 && (
        <SectionCard>
          <Heading size="lg" marginBottom={spacing[4]}>
            {t('admin.excelImport.columnMappings')}
          </Heading>
          <ColumnMappingList>
            {results.data.columnMappings.filter(m => m.mappedPropertyName).map((mapping, index) => (
              <MappingItem key={index}>
                <span><strong>{mapping.excelColumnName}</strong></span>
                <span>→ {mapping.mappedPropertyName}</span>
              </MappingItem>
            ))}
          </ColumnMappingList>
        </SectionCard>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing[4], marginTop: spacing[6] }}>
        <Button
          variant="secondary"
          onClick={() => {
            setFile(null);
            setResults(null);
          }}
          disabled={importing}
        >
          {t('common.clear')}
        </Button>
        <Button
          variant="primary"
          onClick={handleImport}
          disabled={!file || importing}
        >
          {importing ? (
            <>
              <LoadingSpinner size="sm" />
              {t('admin.excelImport.importing')}
            </>
          ) : (
            t('admin.excelImport.import')
          )}
        </Button>
      </div>

      {results && (
        <ResultsCard>
          <Heading size="lg" marginBottom={spacing[4]}>
            {t('admin.excelImport.importResults')}
          </Heading>
          <StatsGrid>
            {results.kind === 'catalog' ? (
              <>
                <StatCard>
                  <StatValue>{results.data.totalRows}</StatValue>
                  <StatLabel>{t('admin.excelImport.totalRows')}</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue style={{ color: theme.colors.success }}>{results.data.successCount}</StatValue>
                  <StatLabel>{t('admin.excelImport.successCount')}</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue style={{ color: theme.colors.error }}>{results.data.errorCount}</StatValue>
                  <StatLabel>{t('admin.excelImport.errorCount')}</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue style={{ color: theme.colors.warning }}>{results.data.skippedCount}</StatValue>
                  <StatLabel>{t('admin.excelImport.skippedCount')}</StatLabel>
                </StatCard>
                {results.data.updatedCount > 0 && (
                  <StatCard>
                    <StatValue style={{ color: theme.colors.info }}>{results.data.updatedCount}</StatValue>
                    <StatLabel>{t('admin.excelImport.updatedCount')}</StatLabel>
                  </StatCard>
                )}
              </>
            ) : (
              <>
                <StatCard>
                  <StatValue>{results.data.totalRows}</StatValue>
                  <StatLabel>{t('admin.excelImport.totalRows')}</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue style={{ color: theme.colors.success }}>{results.data.createdCount}</StatValue>
                  <StatLabel>{t('admin.excelImport.successCount')}</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue style={{ color: theme.colors.info }}>{results.data.updatedCount}</StatValue>
                  <StatLabel>{t('admin.excelImport.updatedCount')}</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{results.data.readyRows}</StatValue>
                  <StatLabel>{t('admin.excelImport.readyRows')}</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue style={{ color: theme.colors.warning }}>{results.data.reviewRequiredRows}</StatValue>
                  <StatLabel>{t('admin.excelImport.reviewRequiredRows')}</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{results.data.exactPaidYearMarkers}</StatValue>
                  <StatLabel>{t('admin.excelImport.exactPaidMarkers')}</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{results.data.paid2025Markers}</StatValue>
                  <StatLabel>{t('admin.excelImport.paid2025Markers')}</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue style={{ color: theme.colors.success }}>{results.data.paymentsCreated}</StatValue>
                  <StatLabel>{t('admin.excelImport.paymentsCreated')}</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue>{results.data.paymentsSkippedExisting}</StatValue>
                  <StatLabel>{t('admin.excelImport.paymentsSkipped')}</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue style={{ color: theme.colors.warning }}>{results.data.rowsWithImportProblems}</StatValue>
                  <StatLabel>{t('admin.excelImport.rowsWithProblems')}</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue style={{ color: theme.colors.error }}>{results.data.errorCount}</StatValue>
                  <StatLabel>{t('admin.excelImport.errorCount')}</StatLabel>
                </StatCard>
              </>
            )}
          </StatsGrid>

          {errors.length > 0 && (
            <div>
              <Heading size="md" marginBottom={spacing[3]}>
                {t('admin.excelImport.errors')}
              </Heading>
              <ErrorTableWrap>
                <ErrorTable>
                  <ErrorTableHeader>
                    <ErrorTableRow>
                      <ErrorTableCell as="th">{t('admin.excelImport.errorRow')}</ErrorTableCell>
                      <ErrorTableCell as="th">{t('admin.excelImport.errorField')}</ErrorTableCell>
                      <ErrorTableCell as="th">{t('admin.excelImport.errorMessage')}</ErrorTableCell>
                    </ErrorTableRow>
                  </ErrorTableHeader>
                  <tbody>
                    {errors.map((error, index) => (
                      <ErrorTableRow key={index}>
                        <ErrorTableCell>{error.rowNumber}</ErrorTableCell>
                        <ErrorTableCell>{error.fieldName || '-'}</ErrorTableCell>
                        <ErrorMessage>{error.errorMessage}</ErrorMessage>
                      </ErrorTableRow>
                    ))}
                  </tbody>
                </ErrorTable>
              </ErrorTableWrap>
            </div>
          )}
        </ResultsCard>
      )}
    </PageContainer>
  );
};

export default ExcelImport;
